import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { toast } from "sonner";
import { Loader2, Save, Wallet } from "lucide-react";

const API = "/api";

/**
 * RC-WEB-3 §6/§7 — control plane for payment-method availability.
 * Same master admin, same `payment_config` collection. Changing a row here changes
 * what the website (and tomorrow Android/iOS) offers, with no code deployment.
 */
export const AdminPaymentMethods = () => {
  const [methods, setMethods] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/payment-methods`)
      .then((r) => setMethods(r.data?.methods || []))
      .catch(() => toast.error("No se pudieron cargar los métodos de pago"));
  }, []);

  const patch = (key, field, value) =>
    setMethods((rows) => rows.map((m) => (m.key === key ? { ...m, [field]: value } : m)));

  const patchLocale = (key, field, value) =>
    setMethods((rows) =>
      rows.map((m) => (m.key === key ? { ...m, [field]: { ...(m[field] || {}), es: value } } : m)),
    );

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/payment-methods`, { methods });
      setMethods(r.data?.methods || methods);
      toast.success("Métodos de pago actualizados. Los clientes lo ven de inmediato.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!methods) {
    return (
      <Card className="border-slate-100 shadow-sm" data-testid="admin-payment-methods-loading">
        <CardContent className="p-6 flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando métodos de pago…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-100 shadow-sm" data-testid="admin-payment-methods">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: "Manrope, sans-serif" }}>
          <Wallet className="w-5 h-5 text-[#1B84FF]" /> Métodos de pago (web · Android · iOS)
        </CardTitle>
        <CardDescription>
          Activo, países, orden, etiqueta, instrucciones y tiempo de confirmación. Un método
          inactivo desaparece del checkout del cliente.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {methods.map((m) => (
          <div
            key={m.key}
            className="rounded-xl border border-slate-200 p-4 space-y-3"
            data-testid={`payment-method-row-${m.key}`}
          >
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-semibold text-[#0B2545]">{(m.labels || {}).es || m.key}</p>
                <p className="text-xs text-slate-500 font-mono">{m.key} · {m.mode}</p>
              </div>
              <div className="flex items-center gap-2">
                <Label htmlFor={`active-${m.key}`} className="text-xs text-slate-600">Activo</Label>
                <Switch
                  id={`active-${m.key}`}
                  checked={!!m.active}
                  onCheckedChange={(v) => patch(m.key, "active", v)}
                  data-testid={`payment-method-active-${m.key}`}
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Países (ISO o *)</Label>
                <Input
                  value={(m.countries || []).join(", ")}
                  onChange={(e) =>
                    patch(m.key, "countries", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))
                  }
                  data-testid={`payment-method-countries-${m.key}`}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Excluir países</Label>
                <Input
                  value={(m.excluded_countries || []).join(", ")}
                  onChange={(e) =>
                    patch(m.key, "excluded_countries", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))
                  }
                  data-testid={`payment-method-excluded-${m.key}`}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-slate-600">Orden</Label>
                <Input
                  type="number"
                  value={m.sort_order ?? 0}
                  onChange={(e) => patch(m.key, "sort_order", Number(e.target.value))}
                  data-testid={`payment-method-order-${m.key}`}
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Etiqueta pública (ES)</Label>
              <Input
                value={(m.labels || {}).es || ""}
                onChange={(e) => patchLocale(m.key, "labels", e.target.value)}
                data-testid={`payment-method-label-${m.key}`}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Instrucciones (ES)</Label>
              <Input
                value={(m.instructions || {}).es || ""}
                onChange={(e) => patchLocale(m.key, "instructions", e.target.value)}
                data-testid={`payment-method-instructions-${m.key}`}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-600">Tiempo de confirmación (ES)</Label>
              <Input
                value={(m.confirmation_eta || {}).es || ""}
                onChange={(e) => patchLocale(m.key, "confirmation_eta", e.target.value)}
                data-testid={`payment-method-eta-${m.key}`}
              />
            </div>
          </div>
        ))}

        <Button
          onClick={save}
          disabled={saving}
          className="w-full h-12 bg-[#1B84FF] hover:bg-[#0F6FDB] text-white font-semibold"
          data-testid="save-payment-methods-btn"
        >
          {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
          Guardar métodos de pago
        </Button>
      </CardContent>
    </Card>
  );
};

export default AdminPaymentMethods;
